<template>
    <kiosk-layout v-if="loading">
        <page-loading />
    </kiosk-layout>

    <kiosk-team-term-details v-else-if="term" :teamId="props.teamId" :term="term">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-lg) justify-center">
            <page-header class="mb-2 md:mb-2">Status Log</page-header>

            <div v-if="logsLoading && !logs.length" class="text-center py-10">
                <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
            </div>

            <template v-if="!logsLoading && !logs.length">
                <empty-state data-test="status-log-empty">No status changes yet.</empty-state>
            </template>

            <vx-table v-if="logs.length" class="lg:grid-cols-3" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>Status</div>
                    <div>Changed by</div>
                    <div>Changed at</div>
                </vx-table-row>

                <vx-table-row v-for="log in logs" :key="log.id" :data-test="`status-log-${log.id}`">
                    <vx-table-row-item>
                        <template v-slot:label>Status:</template>
                        <div class="flex items-center gap-2">
                            <term-status-badge :status="log.from_status" />
                            <font-awesome-icon widthAuto :icon="faArrowRight" class="text-xs text-slate-400" />
                            <term-status-badge :status="log.to_status" />
                        </div>
                    </vx-table-row-item>

                    <vx-table-row-item>
                        <template v-slot:label>Changed by:</template>
                        <div class="flex flex-col">
                            <span>{{ log.user.name || '-' }}</span>
                            <span v-if="log.user.email" class="text-xs text-slate-500">{{ log.user.email }}</span>
                        </div>
                    </vx-table-row-item>

                    <vx-table-row-item>
                        <template v-slot:label>Changed at:</template>
                        {{ shortDateTime(log.created_at) }}
                    </vx-table-row-item>
                </vx-table-row>
            </vx-table>

            <div v-if="logsParams.cursor" class="mt-8">
                <vx-button
                    @click="loadLogs()"
                    color="muted-dark"
                    size="xl"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="logsLoading"
                >
                    Load More
                </vx-button>
            </div>
        </div>
    </kiosk-team-term-details>

    <kiosk-layout v-else>
        <vx-alert color="danger" :closeable="false">Unable to load the Terms.</vx-alert>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { VxAlert, VxButton, VxIconLoading, VxTable, VxTableRow, VxTableRowItem } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { useToasted } from '~/composables/useToasted';
import { adminTermsClient } from '~/services';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { catchRequestError } from '~/utils/request';
import { shortDateTime } from '~/utils/date';
import type { Term, TermStatusLog } from '~/services/adminTermsClient.types';
import KioskTeamTermDetails from './KioskTeamTermDetails.vue';
import TermStatusBadge from './TermStatusBadge.vue';

const props = defineProps<{
    teamId: number;
    termId: number;
}>();

const toasted = useToasted();
const loading = ref(true);
const term = ref<Term | null>(null);

const {
    list: logs,
    loading: logsLoading,
    fetchParams: logsParams,
    fetchRecords: fetchLogs,
} = useCursorPagination<TermStatusLog>();

const loadLogs = () => {
    return fetchLogs(adminTermsClient.getTermStatusLogs(props.teamId, props.termId, logsParams.value));
};

onMounted(async () => {
    logsParams.value = { per_page: 20, cursor: null, sort_dir: 'desc' };
    loadLogs();

    try {
        const response = await adminTermsClient.getTerm(props.teamId, props.termId);
        term.value = response.data;
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        loading.value = false;
    }
});
</script>
